/**
 * How a casting lot and its heats read on screen (the lot list, the lot drawer, a heat's lots). Pure.
 */
import type { DefectWord } from "@/lib/defect-words";

export interface DefectPieces {
  defect: string;
  pieces: number;
}

/** A lot's scrapped pieces per defect, most first, ties by name; none that are zero or below. */
export function defectPieces(defects: Record<string, number> | null | undefined): DefectPieces[] {
  return Object.entries(defects ?? {})
    .filter(([, n]) => Number.isFinite(n) && n > 0)
    .map(([defect, pieces]) => ({ defect, pieces }))
    .sort((a, b) => b.pieces - a.pieces || a.defect.localeCompare(b.defect));
}

/** "cold_iron" → "Cold iron"; with the org's defect words (lib/defect-words), the plant's own word for it. */
export function defectLabel(defect: string, word?: DefectWord): string {
  const s = defect.replace(/_/g, " ").trim();
  const plain = s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
  return word ? word(defect, plain) : plain;
}

/** Which of a lot's keys beyond its day and part a page of lots shows: a column for each one a lot on it carries. */
export interface LotKeyColumns {
  shift: boolean;
  line: boolean;
}

/** The shift and line columns of a page of lots: shown when a lot on it was booked on a shift or a line. */
export function lotKeyColumns(lots: readonly { shift?: string; line?: string }[]): LotKeyColumns {
  return { shift: lots.some((l) => !!l.shift), line: lots.some((l) => !!l.line) };
}

/** What one lot is, as the plant books it: "A lot is one part cast on one shift and line of one day". */
export function lotUnitSentence(cols: LotKeyColumns): string {
  if (cols.shift && cols.line) return "A lot is one part cast on one shift and line of one day";
  if (cols.shift) return "A lot is one part cast on one shift of one day";
  if (cols.line) return "A lot is one part cast on one line on one day";
  return "A lot is one part cast on one day";
}

/** Scrapped over cast as a percentage to one decimal, or "—" without castings or with an unknown scrap. */
export function scrapPct(cast: number | null | undefined, scrap: number | null | undefined): string {
  if (cast == null || scrap == null || cast <= 0 || scrap < 0) return "—";
  return `${((scrap / cast) * 100).toFixed(1)}%`;
}

/** The chemistry a foundry reads first, in its order; the rest follow by name. */
const CHEM_ORDER = ["C", "CE", "Si", "Mn", "S", "P", "residual_mg", "Mg", "chromium", "Cr", "copper", "Cu", "tin", "Sn"];

export interface ChemLine {
  param: string;
  mean: number;
  unit: string;
  n: number;
}

/** Long element names an import may carry, folded onto the symbols CHEM_ORDER ranks (the priors aliases' forms). */
const CHEM_SYMBOL: Record<string, string> = {
  carbon: "C",
  carbon_equivalent: "CE",
  silicon: "Si",
  manganese: "Mn",
  sulfur: "S",
  sulphur: "S",
  phosphorus: "P",
  magnesium: "Mg",
};

/** A heat's ladle chemistry in reading order, the elements a foundry reads first leading, whatever name they carry. */
export function orderChemistry<T extends ChemLine>(params: readonly T[]): T[] {
  const rank = (p: string) => {
    const i = CHEM_ORDER.indexOf(CHEM_SYMBOL[p.toLowerCase()] ?? p);
    return i < 0 ? CHEM_ORDER.length : i;
  };
  return [...params].sort((a, b) => rank(a.param) - rank(b.param) || a.param.localeCompare(b.param));
}

/** "C 3.52 · Si 2.10 (8 ladles)", the first few of a heat's chemistry for a one-line summary. */
export function chemistrySummary(params: readonly ChemLine[], max = 4): string {
  const ordered = orderChemistry(params).slice(0, max);
  if (ordered.length === 0) return "";
  const n = Math.max(...ordered.map((p) => p.n));
  const parts = ordered.map((p) => `${p.param} ${fmtChem(p.mean)}${p.unit && p.unit !== "%" ? ` ${p.unit}` : ""}`);
  return `${parts.join(" · ")}${n > 1 ? ` (${n} ladles)` : ""}`;
}

function fmtChem(v: number): string {
  if (!Number.isFinite(v)) return "—";
  const a = Math.abs(v);
  if (a >= 100) return v.toFixed(0);
  if (a >= 10) return v.toFixed(1);
  if (a >= 0.1) return v.toFixed(2);
  return v.toFixed(3);
}
